import type { PresenceFilter, ReviewSearchParamsDto } from '@/contracts/marketplace/reviews';
import { SortDirection } from '@/enums/sort';
import type { Sort } from '@/types/sort.type';
import { normalizeReviewFilters } from '@/shared/review-filters-normalization';

export const getReviewSelectionSessionStorageKey = (clientId: string): string => `review-selection-session:${clientId}`;
export const getReviewPageStorageKey = (clientId: string): string => `review-page:${clientId}`;
export const getReviewRowsOnPageStorageKey = (clientId: string): string => `review-rows-on-page:${clientId}`;
export const getReviewFiltersStorageKey = (clientId: string): string => `review-filters:${clientId}`;
export const getReviewSortStorageKey = (clientId: string): string => `review-sort:${clientId}`;

export const getProductsPageStorageKey = (clientId: string): string => `products-page:${clientId}`;
export const getProductsRowsOnPageStorageKey = (clientId: string): string => `products-rows-on-page:${clientId}`;
export const getProductsNameFilterStorageKey = (clientId: string): string => `products-name-filter:${clientId}`;
export const getProductsProductFilterStorageKey = (clientId: string): string => `products-product-filter:${clientId}`;
export const getProductsBrandFilterStorageKey = (clientId: string): string => `products-brand-filter:${clientId}`;
export const getProductsBrandSearchStorageKey = (clientId: string): string => `products-brand-search:${clientId}`;
export const getProductsPhotosFilterStorageKey = (clientId: string): string => `products-photos-filter:${clientId}`;
export const getProductsSortStorageKey = (clientId: string): string => `products-sort:${clientId}`;

export const getBrandsPageStorageKey = (clientId: string): string => `brands-page:${clientId}`;
export const getBrandsRowsOnPageStorageKey = (clientId: string): string => `brands-rows-on-page:${clientId}`;
export const getBrandsNameFilterStorageKey = (clientId: string): string => `brands-name-filter:${clientId}`;
export const getBrandsBrandFilterStorageKey = (clientId: string): string => `brands-brand-filter:${clientId}`;
export const getBrandsSortStorageKey = (clientId: string): string => `brands-sort:${clientId}`;
export const getBrandsSelectionStorageKey = (clientId: string): string => `brands-selection:${clientId}`;

export const DEFAULT_REVIEWS_PAGE = 1;
export const DEFAULT_TABLE_ROWS_ON_PAGE = 10;

type BuildSelectionSessionIdOptions = {
    randomUuid?: (() => string) | undefined;
    now?: () => number;
    random?: () => number;
};

export const buildSelectionSessionId = ({
    randomUuid,
    now = Date.now,
    random = Math.random,
}: BuildSelectionSessionIdOptions = {}): string => {
    if (randomUuid) {
        return randomUuid();
    }

    return `${now()}-${random().toString(16).slice(2)}`;
};

export const resolveReviewSelectionSessionId = (
    existingSessionId: string | null,
    options?: BuildSelectionSessionIdOptions,
): string => {
    if (existingSessionId) {
        return existingSessionId;
    }

    return buildSelectionSessionId(options);
};

export const normalizeReviewPage = (value: unknown): number => {
    const parsed = Number(value);

    if (!Number.isInteger(parsed) || parsed < DEFAULT_REVIEWS_PAGE) {
        return DEFAULT_REVIEWS_PAGE;
    }

    return parsed;
};

export const readStoredReviewPage = (clientId: string): number => {
    if (typeof window === 'undefined') {
        return DEFAULT_REVIEWS_PAGE;
    }

    return normalizeReviewPage(window.sessionStorage.getItem(getReviewPageStorageKey(clientId)));
};

export const writeStoredReviewPage = (clientId: string, value: number): number => {
    const normalizedPage = normalizeReviewPage(value);

    if (typeof window === 'undefined') {
        return normalizedPage;
    }

    window.sessionStorage.setItem(getReviewPageStorageKey(clientId), String(normalizedPage));

    return normalizedPage;
};

const normalizeRowsOnPage = (value: unknown): number => {
    const parsed = Number(value);

    if (!Number.isInteger(parsed) || parsed < 1) {
        return DEFAULT_TABLE_ROWS_ON_PAGE;
    }

    return parsed;
};

const readStoredString = (storageKey: string): string | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    return window.sessionStorage.getItem(storageKey);
};

const writeStoredString = (storageKey: string, value: string): void => {
    if (typeof window === 'undefined') {
        return;
    }

    window.sessionStorage.setItem(storageKey, value);
};

const readStoredJson = (storageKey: string): unknown => {
    const rawValue = readStoredString(storageKey);

    if (!rawValue) {
        return null;
    }

    try {
        return JSON.parse(rawValue);
    } catch {
        return null;
    }
};

const normalizeOptionalString = (value: unknown): string | undefined => {
    if (typeof value !== 'string') {
        return undefined;
    }

    const normalized = value.trim();
    return normalized.length ? normalized : undefined;
};

const normalizeString = (value: unknown): string => (typeof value === 'string' ? value : '');

const normalizeStringArray = (value: unknown): string[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    const normalizedValues: string[] = [];
    const seenValues = new Set<string>();

    for (const item of value) {
        const normalizedValue = normalizeOptionalString(item);

        if (!normalizedValue || seenValues.has(normalizedValue)) {
            continue;
        }

        seenValues.add(normalizedValue);
        normalizedValues.push(normalizedValue);
    }

    return normalizedValues;
};

const normalizePresenceFilter = (value: unknown): PresenceFilter | undefined =>
    value === 'with' || value === 'without' ? value : undefined;

const normalizeSort = (value: unknown): Sort => {
    if (!value || typeof value !== 'object') {
        return {};
    }

    const rawSort = value as Record<string, unknown>;
    const sortKey = normalizeOptionalString(rawSort.sortKey);
    const sortOrder =
        rawSort.sortOrder === SortDirection.ASC || rawSort.sortOrder === SortDirection.DESC
            ? rawSort.sortOrder
            : undefined;

    if (!sortKey || !sortOrder) {
        return {};
    }

    return {
        sortKey,
        sortOrder,
    };
};

export const readStoredReviewsOnPage = (clientId: string): number =>
    normalizeRowsOnPage(readStoredString(getReviewRowsOnPageStorageKey(clientId)));

export const writeStoredReviewsOnPage = (clientId: string, value: number): number => {
    const normalizedRowsOnPage = normalizeRowsOnPage(value);
    writeStoredString(getReviewRowsOnPageStorageKey(clientId), String(normalizedRowsOnPage));
    return normalizedRowsOnPage;
};

export const readStoredReviewFilters = (clientId: string): ReviewSearchParamsDto =>
    normalizeReviewFilters(readStoredJson(getReviewFiltersStorageKey(clientId)) || {});

export const writeStoredReviewFilters = (clientId: string, filters: ReviewSearchParamsDto): ReviewSearchParamsDto => {
    const normalizedFilters = normalizeReviewFilters(filters);
    writeStoredString(getReviewFiltersStorageKey(clientId), JSON.stringify(normalizedFilters));
    return normalizedFilters;
};

export const readStoredReviewSort = (clientId: string): Sort =>
    normalizeSort(readStoredJson(getReviewSortStorageKey(clientId)));

export const writeStoredReviewSort = (clientId: string, sort: Sort): Sort => {
    const normalizedSort = normalizeSort(sort);
    writeStoredString(getReviewSortStorageKey(clientId), JSON.stringify(normalizedSort));
    return normalizedSort;
};

export const readStoredProductsPage = (clientId: string): number =>
    normalizeReviewPage(readStoredString(getProductsPageStorageKey(clientId)));

export const writeStoredProductsPage = (clientId: string, value: number): number => {
    const normalizedPage = normalizeReviewPage(value);
    writeStoredString(getProductsPageStorageKey(clientId), String(normalizedPage));
    return normalizedPage;
};

export const readStoredProductsOnPage = (clientId: string): number =>
    normalizeRowsOnPage(readStoredString(getProductsRowsOnPageStorageKey(clientId)));

export const writeStoredProductsOnPage = (clientId: string, value: number): number => {
    const normalizedRowsOnPage = normalizeRowsOnPage(value);
    writeStoredString(getProductsRowsOnPageStorageKey(clientId), String(normalizedRowsOnPage));
    return normalizedRowsOnPage;
};

export const readStoredProductsNameFilter = (clientId: string): string =>
    normalizeString(readStoredString(getProductsNameFilterStorageKey(clientId)));

export const writeStoredProductsNameFilter = (clientId: string, value: string): string => {
    const normalizedValue = normalizeString(value);
    writeStoredString(getProductsNameFilterStorageKey(clientId), normalizedValue);
    return normalizedValue;
};

export const readStoredProductsProductFilter = (clientId: string): string =>
    normalizeString(readStoredString(getProductsProductFilterStorageKey(clientId)));

export const writeStoredProductsProductFilter = (clientId: string, value: string): string => {
    const normalizedValue = normalizeString(value);
    writeStoredString(getProductsProductFilterStorageKey(clientId), normalizedValue);
    return normalizedValue;
};

export const readStoredProductsBrandFilter = (clientId: string): string | undefined =>
    normalizeOptionalString(readStoredString(getProductsBrandFilterStorageKey(clientId)));

export const writeStoredProductsBrandFilter = (clientId: string, value: string | undefined): string | undefined => {
    const normalizedValue = normalizeOptionalString(value);
    writeStoredString(getProductsBrandFilterStorageKey(clientId), normalizedValue || '');
    return normalizedValue;
};

export const readStoredProductsBrandSearch = (clientId: string): string | undefined =>
    normalizeOptionalString(readStoredString(getProductsBrandSearchStorageKey(clientId)));

export const writeStoredProductsBrandSearch = (clientId: string, value: string | undefined): string | undefined => {
    const normalizedValue = normalizeOptionalString(value);
    writeStoredString(getProductsBrandSearchStorageKey(clientId), normalizedValue || '');
    return normalizedValue;
};

export const readStoredProductsPhotosFilter = (clientId: string): PresenceFilter | undefined =>
    normalizePresenceFilter(readStoredString(getProductsPhotosFilterStorageKey(clientId)));

export const writeStoredProductsPhotosFilter = (
    clientId: string,
    value: PresenceFilter | undefined,
): PresenceFilter | undefined => {
    const normalizedValue = normalizePresenceFilter(value);
    writeStoredString(getProductsPhotosFilterStorageKey(clientId), normalizedValue || '');
    return normalizedValue;
};

export const readStoredProductsSort = (clientId: string): Sort =>
    normalizeSort(readStoredJson(getProductsSortStorageKey(clientId)));

export const writeStoredProductsSort = (clientId: string, sort: Sort): Sort => {
    const normalizedSort = normalizeSort(sort);
    writeStoredString(getProductsSortStorageKey(clientId), JSON.stringify(normalizedSort));
    return normalizedSort;
};

export const readStoredBrandsPage = (clientId: string): number =>
    normalizeReviewPage(readStoredString(getBrandsPageStorageKey(clientId)));

export const writeStoredBrandsPage = (clientId: string, value: number): number => {
    const normalizedPage = normalizeReviewPage(value);
    writeStoredString(getBrandsPageStorageKey(clientId), String(normalizedPage));
    return normalizedPage;
};

export const readStoredBrandsOnPage = (clientId: string): number =>
    normalizeRowsOnPage(readStoredString(getBrandsRowsOnPageStorageKey(clientId)));

export const writeStoredBrandsOnPage = (clientId: string, value: number): number => {
    const normalizedRowsOnPage = normalizeRowsOnPage(value);
    writeStoredString(getBrandsRowsOnPageStorageKey(clientId), String(normalizedRowsOnPage));
    return normalizedRowsOnPage;
};

export const readStoredBrandsNameFilter = (clientId: string): string =>
    normalizeString(readStoredString(getBrandsNameFilterStorageKey(clientId)));

export const writeStoredBrandsNameFilter = (clientId: string, value: string): string => {
    const normalizedValue = normalizeString(value);
    writeStoredString(getBrandsNameFilterStorageKey(clientId), normalizedValue);
    return normalizedValue;
};

export const readStoredBrandsBrandFilter = (clientId: string): string =>
    normalizeString(readStoredString(getBrandsBrandFilterStorageKey(clientId)));

export const writeStoredBrandsBrandFilter = (clientId: string, value: string): string => {
    const normalizedValue = normalizeString(value);
    writeStoredString(getBrandsBrandFilterStorageKey(clientId), normalizedValue);
    return normalizedValue;
};

export const readStoredPickedBrandGroups = (clientId: string): string[] =>
    normalizeStringArray(readStoredJson(getBrandsSelectionStorageKey(clientId)));

export const writeStoredPickedBrandGroups = (clientId: string, value: string[]): string[] => {
    const normalizedValue = normalizeStringArray(value);
    writeStoredString(getBrandsSelectionStorageKey(clientId), JSON.stringify(normalizedValue));
    return normalizedValue;
};

export const readStoredBrandsSort = (clientId: string): Sort =>
    normalizeSort(readStoredJson(getBrandsSortStorageKey(clientId)));

export const writeStoredBrandsSort = (clientId: string, sort: Sort): Sort => {
    const normalizedSort = normalizeSort(sort);
    writeStoredString(getBrandsSortStorageKey(clientId), JSON.stringify(normalizedSort));
    return normalizedSort;
};

export const reconcilePickedIds = (pickedIds: string[], availableIds: Set<string>): string[] => {
    if (!pickedIds.length) {
        return pickedIds;
    }

    const nextPickedIds = pickedIds.filter((id) => availableIds.has(id));
    return nextPickedIds.length === pickedIds.length ? pickedIds : nextPickedIds;
};
